import React, { useId, useMemo, useRef, useState } from 'react'
import { mergeProps } from 'react-aria'
import { SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import { PositioningProps } from '../../types'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { Popover } from '../../../core-components/popover/Popover'
import { Box } from '../../../core-components'
import { useElementScrolledOut } from '../../../internal-helpers/useElementScrolledOut'
import { useElementSize, useFocusVisible } from '../../hooks'
import { Expandable, useExpandable } from '../../input/pill-combobox/Expandable'
import { MIN_SELECT_LIST_WIDTH } from '../base-select/constants'
import { useSelectListData } from '../select-list/useSelectListData'
import { Label, Pills, SelectContainer } from './PillsSelectContent'
import { PillSelectList } from './PillSelectList'

type Variant = 'regular' | 'nested'

type Props = {
  INTERNAL_isFocusVisible?: boolean
  INTERNAL_isTriggerOpen?: boolean
  children: React.ReactNode
  'data-test-id'?: string
  isDisabled?: boolean
  isExpandable?: boolean
  label: React.ReactNode
  onChange: (value: Array<string>) => void
  onSearchChange?: (value: string) => void
  placeholder?: string
  search?: string
  values: ReadonlyArray<string>
  variant?: Variant
} & PositioningProps

// TODO: Refactor in the future to make it accessible
export const PillSelect = ({
  values,
  children,
  label,
  onChange,
  isDisabled,
  INTERNAL_isFocusVisible,
  'data-test-id': dataTestId,
  placeholder = 'Select...',
  variant = 'regular',
  INTERNAL_isTriggerOpen = false,
  onSearchChange,
  search,
  isExpandable,
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'PillSelect')
  const labelId = useId()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const triggerRef = useRef<HTMLDivElement>(null)
  const { width: triggerWidth } = useElementSize(triggerRef)

  const listData = useSelectListData({
    children,
    componentName: 'PillSelect',
    search,
  })

  const expandableWrapperRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  useElementScrolledOut(wrapperRef, () => {
    setIsPopoverOpen(false)
  })
  const valuesWithMatchingOptions = useMemo(
    () => values.filter(value => listData.existingValues.has(value)),
    [values, listData]
  )
  const { expandableProps, visibleValues, isExpanded, invisibleValuesCount, isMeasuring } =
    useExpandable(
      valuesWithMatchingOptions,
      isExpandable,
      expandableWrapperRef,
      1,
      0,
      isPopoverOpen
    )
  const isOpen = isPopoverOpen || INTERNAL_isTriggerOpen

  const { focusProps, isFocusVisible } = useFocusVisible(INTERNAL_isFocusVisible)

  return (
    <SettingLabelContainer
      isRequired={false}
      label={label}
      labelProps={{ id: labelId }}
      labelElement='div'
      {...positioningProps}
    >
      <Popover
        placement='bottom start'
        offset={8}
        isOpen={isOpen}
        onOpenChange={newIsOpen => {
          setIsPopoverOpen(newIsOpen)
          if (!newIsOpen) {
            onSearchChange?.('')
          }
        }}
        trigger={triggerProps => {
          const commonProps = {
            rootProps: expandableProps,
            ariaProps: mergeProps(triggerProps, focusProps, { 'aria-labelledby': labelId }),
            variant,
            isOpen,
            isDisabled,
            isFocusVisible,
          }
          const commonPillsProps = {
            visibleValues: visibleValues.map(value => ({ ...listData.itemData[value], value })),
            values,
            onChange,
            isDisabled,
            placeholder,
          }
          return (
            <Box ref={wrapperRef}>
              <Expandable when={isExpanded} variant={variant}>
                <SelectContainer {...commonProps} data-test-id={dataTestId} triggerRef={triggerRef}>
                  <Pills
                    {...commonPillsProps}
                    pillsWrapperRef={expandableWrapperRef}
                    invisibleValuesCount={invisibleValuesCount}
                    isMeasuring={isMeasuring}
                    isWrapping={!isExpandable || isExpanded || isMeasuring}
                  />
                </SelectContainer>
              </Expandable>
            </Box>
          )
        }}
      >
        {!INTERNAL_isTriggerOpen && (
          <Box
            minWidth={Math.max(triggerWidth, MIN_SELECT_LIST_WIDTH)}
            maxHeight='inherit'
            overflow='hidden'
            borderRadius={8}
          >
            <PillSelectList
              onClose={() => setIsPopoverOpen(false)}
              aria-labelledby={labelId}
              onChange={onChange}
              values={values}
              onSearchChange={onSearchChange}
              search={search}
            >
              {children}
            </PillSelectList>
          </Box>
        )}
      </Popover>
    </SettingLabelContainer>
  )
}

PillSelect.Label = Label
PillSelect.Item = PillSelectList.Item
PillSelect.StartContent = PillSelectList.StartContent
PillSelect.EndContent = PillSelectList.EndContent
PillSelect.Section = PillSelectList.Section
